import { useEffect, useRef, useState } from 'react';
import { ImagePlus, Pause, Play } from 'lucide-react';

const STEP_MS = 3200;
// d = desktop studio screenshot, m = phone screenshot; x/y = cursor target in percent of the frame.
const STEPS = [
  { title: 'Artikel wählen', text: 'T-Shirt öffnen – Vorder- und Rückseite inklusive.', img: 1, d: [3.4, 28.3], m: [30.8, 96] },
  { title: 'Kinderzeichnung hochladen', text: 'Foto der Zeichnung hochladen, zuschneiden, platzieren.', img: 3, d: [41.1, 57.8], m: [50, 39] },
  { title: 'Namen dazuschreiben', text: 'Text tippen, Handschrift wählen – live auf dem Shirt.', img: 4, d: [41.1, 63.9], m: [50, 58], type: 'Unsere Familie' },
  { title: 'Rückseite gestalten', text: 'Mit einem Tipp auf die Rückseite – z. B. „PAPA“.', img: 5, d: [45.3, 28.8], m: [67.2, 10], type: 'PAPA' },
  { title: 'Vorschau prüfen', text: 'Alle Seiten groß ansehen, bevor du bestellst.', img: 6, d: [66.3, 39.1], m: [84.6, 27.5] },
  { title: 'Fertig – dein Unikat', text: 'Wir drucken in Deutschland und schicken es dir zu.', photo: '/images/platform/hero-kinderzeichnung.webp' },
];

const useTyped = (text, active) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    setN(0);
    if (!active || !text) return undefined;
    const t = setInterval(() => setN(v => (v >= text.length ? v : v + 1)), 70);
    return () => clearInterval(t);
  }, [text, active]);
  return text ? text.slice(0, n) : '';
};

const useInView = ref => {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setSeen(e.isIntersecting), { threshold: .35 });
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [ref]);
  return seen;
};

const Screen = ({ step, index, kind, typed }) => <div className="seq-screen">
  {STEPS.map((s, i) => <img key={i} src={s.photo || `/images/platform/seq/${kind}${s.img}.webp`} alt={i === index ? `${s.title} im ManuCreator-Studio` : ''} className={`${i === index ? 'on' : ''} ${s.photo ? 'is-photo' : ''}`} style={s[kind] ? { transformOrigin: `${s[kind][0]}% ${s[kind][1]}%` } : undefined} loading="lazy" data-testid={`seq-frame-${kind}-${i}`} />)}
  {step[kind] && <span className="seq-cursor" style={{ left: `${step[kind][0]}%`, top: `${step[kind][1]}%` }} data-testid={`seq-cursor-${kind}`}><svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M4 2l15 10-6.5 1.2L16 20l-2.6 1.2-3.4-6.6L5 19z" fill="#1c2b22" stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" /></svg><i key={index} />{step.type && <em className="seq-typing" data-testid={`seq-typing-${kind}`}>{typed}<b /></em>}</span>}
</div>;

export const DesignerSequence = () => {
  const ref = useRef(null), visible = useInView(ref), [index, setIndex] = useState(0), [playing, setPlaying] = useState(true);
  const reduced = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const step = STEPS[index], typed = useTyped(step.type, visible);
  useEffect(() => {
    if (!visible || !playing || reduced) return undefined;
    const t = setTimeout(() => setIndex(i => (i + 1) % STEPS.length), index === STEPS.length - 1 ? STEP_MS + 1200 : STEP_MS);
    return () => clearTimeout(t);
  }, [index, visible, playing, reduced]);
  return <div className="seq" ref={ref} data-testid="designer-sequence">
    <div className="seq-devices">
      <div className="laptop seq-laptop"><div className="laptop-screen"><Screen step={step} index={index} kind="d" typed={typed} /></div><div className="laptop-base" /></div>
      <div className="seq-phone"><Screen step={step} index={index} kind="m" typed={typed} /></div>
    </div>
    <div className="seq-caption" aria-live="polite">
      <span className="seq-no">{index + 1}/{STEPS.length}</span>
      <div><strong data-testid="seq-title">{step.title}</strong><small>{step.text}</small></div>
      <button type="button" onClick={() => setPlaying(!playing)} aria-label={playing ? 'Animation anhalten' : 'Animation abspielen'} data-testid="seq-toggle">{playing ? <Pause size={15} /> : <Play size={15} />}</button>
    </div>
    <ol className="seq-steps" aria-label="Schritte">{STEPS.map((s, i) => <li key={s.title}><button type="button" className={`${i === index ? 'on' : ''} ${i < index ? 'done' : ''}`} onClick={() => setIndex(i)} aria-label={`Schritt ${i + 1}: ${s.title}`} data-testid={`seq-step-${i}`}><i style={{ animationDuration: `${STEP_MS}ms`, animationPlayState: visible && playing && !reduced ? 'running' : 'paused' }} /></button></li>)}</ol>
    <p className="seq-hint"><ImagePlus size={14} />Echte Aufnahmen aus unserem Studio</p>
  </div>;
};
